import { Button, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AncillarySubtype, useRatePlanQueryObserver } from "../../../../api/services";
import DraftTimer from "../../_components/DraftTimer";
import { AdditionalDriversDetails, BasicDetails, BookingOwnerDetails, ExtraServices, FirstDriverDetails, InvoiceDetails, TotalToBePaid } from "../../sections";
import { IFormValues } from "../../types";
import ButtonBook from "./buttons/ButtonBook";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { ReactQueryKey, RentalType } from "../../../../types";
import ButtonOffer from "./buttons/ButtonOffer";
import { BillingAddressDetails } from "../../sections/billing-address/BillingAddressDetails";
import { useRecoilState } from "recoil";
import { isOfferState } from "../../recoil";
import { hasEffectiveSubtype } from "../../../../../shared/coverageMutualExclusion";

const CustomerDetails = () => {

    const navigate = useNavigate();

    const { t } = useTranslation('booking');

    const [isOffer] = useRecoilState(isOfferState);

    const additionalDriversAncillary = useWatch<IFormValues>({
        name: `extraServices.ancillary.${AncillarySubtype.AdditionalDrivers}`
    });

    // LPP-21950: conteggio driver inclusi nei Pack selezionati
    const formContext = useFormContext<IFormValues>();
    const queryClient = useQueryClient();
    const tenant = useWatch<IFormValues>({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch<IFormValues>({ control: formContext.control, name: "basicDetails.dealers" });
    const ratePlanData = queryClient.getQueryData<any>([ReactQueryKey.RatePlan, {tenant: tenant as string, dealer: dealer as string}]);
    const ancillaryValues = useWatch<IFormValues>({ name: "extraServices.ancillary" }) as Record<string, number | boolean | undefined> | undefined;

    const totalAdditionalDrivers = useMemo(() => {
        const standalone = typeof additionalDriversAncillary === 'number' ? additionalDriversAncillary : (additionalDriversAncillary ? 1 : 0);
        if (!ancillaryValues || !ratePlanData?.ancillaries) return standalone;
        const fromPacks = ratePlanData.ancillaries
            .filter((a: any) => {
                const val = ancillaryValues[a.ancillaryCode];
                return (val === true || (typeof val === 'number' && val > 0)) && a.subtype !== AncillarySubtype.AdditionalDrivers;
            })
            .filter((a: any) => hasEffectiveSubtype(a, 'ADDITIONAL_DRIVER'))
            .reduce((sum: number, a: any) => {
                const val = ancillaryValues[a.ancillaryCode];
                return sum + (typeof val === 'number' ? val : 1);
            }, 0);
        return standalone + fromPacks;
    }, [additionalDriversAncillary, ancillaryValues, ratePlanData]);

    // LPP-21950: inizializza l'array additionalDrivers nel form se il totale viene dai Pack
    useEffect(() => {
        if (totalAdditionalDrivers > 0) {
            const currentDrivers = formContext.getValues('additionalDrivers') ?? [];
            if (currentDrivers.length < totalAdditionalDrivers) {
                const newDrivers = [];
                for (let i = 0; i < totalAdditionalDrivers; i++) {
                    newDrivers.push(currentDrivers[i] ?? { id: '', name: '', surname: '', email: '' });
                }
                formContext.setValue('additionalDrivers', newDrivers);
            }
        }
    }, [totalAdditionalDrivers]);

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    const handleReset = useCallback(() => {
        window.history.replaceState({}, document.title);
        navigate(0);
    }, []);

    const isFetchingMobilePrefixes = useIsFetching({
        queryKey: [ReactQueryKey.MobilePrefixes]
    });
    const isFetchingCountries = useIsFetching({
        queryKey: [ReactQueryKey.Countries]
    });

    return (
        <Grid container spacing={2} alignItems="start">
            <Grid container item xs={12} sm={12} md={6} spacing={2}>
                <Grid item xs={12}>
                    <BasicDetails />
                </Grid>
                <Grid item xs={12}>
                    <ExtraServices />
                </Grid>
                <Grid item xs={12}>
                    <TotalToBePaid />
                </Grid>
            </Grid>
            <Grid container item xs={12} sm={12} md={6} spacing={2}>
                <Grid item xs={12}>
                    <FirstDriverDetails />
                </Grid>
                {/*Will be shown only if isOffer is true*/}
                <Grid item xs={12}>
                    <BillingAddressDetails />
                </Grid>
                {rentalType === RentalType.CORPORATE &&
                    <Grid item xs={12}>
                        <InvoiceDetails/>
                    </Grid>
                }
                <Grid item xs={12}>
                    <BookingOwnerDetails />
                </Grid>
                {totalAdditionalDrivers ?
                    <Grid item xs={12}>
                        <AdditionalDriversDetails />
                    </Grid>
                    : undefined
                }
                <Grid container item mt={1} justifyContent="end" columnGap={2}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                    >
                        {t('label.reset')}
                    </Button>
                    {isOffer && <ButtonOffer />}
                    {!isOffer && <ButtonBook />}
                </Grid>
            </Grid>
            <DraftTimer />
            <CustomLoader
                loading={
                    !!isFetchingMobilePrefixes ||
                    !!isFetchingCountries
                }
            />
        </Grid>
    );
};

export default CustomerDetails;
